import { ComponentProps, forwardRef, ReactNode } from 'react'

import { clsx } from 'clsx'

import { CheckIcon } from '../icons/index.js'
import { runIfFn } from '../utils/index.js'
import { useMultiselect } from './MultiselectContext.js'
import { hasId } from './util.js'

interface MultiselectItemRenderProps {
  selected: boolean
  active: boolean
}

export type MultiselectItemProps<T = unknown> = Omit<
  ComponentProps<'li'>,
  'value' | 'css' | 'children'
> & {
  value: T
  index: number
  disabled?: boolean
  children?: ReactNode | ((props: MultiselectItemRenderProps) => ReactNode)
}
export const MultiselectItem = forwardRef<HTMLLIElement, MultiselectItemProps>(
  (props, ref) => {
    const { value, index, children, disabled, className, ...rest } = props

    const { selectedItems, getItemProps, highlightedIndex } = useMultiselect()

    let selected
    if (hasId(value)) {
      selected = selectedItems.some(it => hasId(it) && it.id === value.id)
    } else {
      selected = selectedItems.includes(value)
    }

    const active = highlightedIndex === index

    return (
      <li
        className={clsx(
          active
            ? 'bg-action-primary text-content-on-primary'
            : 'bg-surface text-content',
          'cursor-default select-none relative py-2 ps-3 pe-9',
          className,
        )}
        {...getItemProps({ ref, item: value, index, disabled, ...rest })}
      >
        <>
          <span
            className={clsx(
              selected ? 'font-semibold' : 'font-normal',
              disabled ? 'opacity-50' : '',
              'block truncate',
            )}
          >
            {runIfFn(children, {
              selected,
              active,
            })}
          </span>

          {selected ? (
            <span
              className={clsx(
                active ? 'text-content-on-primary' : 'text-content-subdued',
                'absolute inset-y-0 end-0 flex items-center pe-4',
              )}
            >
              <CheckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      </li>
    )
  },
)
